import axios, { AxiosRequestConfig } from 'axios';
import { CacheKeys } from 'utils/cacheKeys';
import { confirmDialog, getCookie } from './helperFunctions';
import { BACKEND_URL } from 'env';

export const client = axios.create({
  baseURL: BACKEND_URL
});

const mergeHeaders = (
  defaultHeaders: Record<string, string>,
  customHeaders: Record<string, string> = {}
): Record<string, string> => {
  return { ...defaultHeaders, ...customHeaders };
};

const _request = async (url: string, { method, data, params, headers = {} }: AxiosRequestConfig) => {
  const token = getCookie(CacheKeys.authToken);
  const defaultHeaders: Record<string, string> = {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  const mergedHeaders = mergeHeaders(defaultHeaders, headers as Record<string, string>);

  try {
    const response = await client({
      url,
      method,
      data,
      params,
      headers: mergedHeaders
    });
    return response?.data;
  } catch (error) {
    console.error('API call failed:', error);
  }
};

const http = {
  get: async (url: string, params?: object) => _request(url, { method: 'GET', params }),
  post: async (url: string, data?: object | FormData) => {
    if (data instanceof FormData) {
      return _request(url, { method: 'POST', data, headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      return _request(url, { method: 'POST', data });
    }
  },
  put: async (url: string, data?: object | FormData) => {
    if (data instanceof FormData) {
      return _request(url, { method: 'PUT', data, headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      return _request(url, { method: 'PUT', data });
    }
  },
  patch: async (url: string, data?: object | FormData) => {
    if (data instanceof FormData) {
      return _request(url, { method: 'PATCH', data, headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      return _request(url, { method: 'PATCH', data });
    }
  },
  delete: async (url: string, data?: object) => {
    const result = await confirmDialog({
      title: 'Are you sure?',
      text: 'Are you sure you want to delete this item? This action cannot be undone.',
      icon: 'warning',
      iconColor: '#DA0808',
      confirmButtonColor: '#DA0808',
      cancelButtonColor: 'transparent',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      return _request(url, { method: 'DELETE', data });
    } else {
      return Promise.resolve(console.log('delete action was cancelled.'));
    }
  },
  getFile: async (url: string, params?: object) =>
    _request(url, { method: 'GET', params, headers: { responseType: 'blob' } })
};

export default http;
